import type { RunAggregateProjectionV2, WebTestRun } from '@blume/web-test-model'
import { ChevronRight } from 'lucide-react'
import { useMemo } from 'react'

import { relativeTime } from '@/design/format'
import { classificationMeta, runVerdict, RunStatusBadge } from '@/design/status'
import { SuiteAvatarTile, AvatarTile, scenarioEmoji } from '@/ui/atoms/avatar-tile'
import { Card, CardContent, CardHeader } from '@/components/ui/card'

import {
  independentSuiteKey,
  scenarioHref,
  StatusDistribution,
  suiteSummaries
} from './entity-shared'
import { BackLink, MetaLine, RunList, SectionLabel } from './parts'

/** One scenario is the suite's primary narrative; its runs are contextual evidence. */
function ScenarioEvidenceGroup({
  scenarioId,
  runs,
  now
}: {
  scenarioId: string
  runs: WebTestRun[]
  now: number
}) {
  const latest = [...runs].sort(
    (left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
  )[0]!
  const verdict = runVerdict(latest)
  const classification = latest.classification
    ? classificationMeta[latest.classification]
    : undefined
  const meta =
    classification && (latest.status === 'failed' || classification.label === verdict.label)
      ? classification
      : verdict
  return (
    <li>
      <Card className="gap-0 rounded-lg py-0 shadow-none">
        <CardHeader className="gap-2 px-4 pt-4 pb-3 sm:px-5">
          <div className="flex items-center gap-3">
            <AvatarTile id={scenarioId} emoji={scenarioEmoji(scenarioId)} size="regular" />
            <div className="min-w-0 flex-1">
              <a
                href={scenarioHref(scenarioId)}
                className="group inline-flex min-h-7 items-center gap-1.5 rounded-md font-mono text-[13px] font-semibold hover:underline focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
              >
                {scenarioId}
                <ChevronRight aria-hidden className="size-3.5 text-muted-foreground" />
                <span className="sr-only"> — open scenario page</span>
              </a>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                <RunStatusBadge meta={meta} active={verdict.active} />
                <span className="text-[13px] text-muted-foreground">
                  {runs.length} loaded run{runs.length === 1 ? '' : 's'} as evidence
                </span>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="border-t px-4 py-2 sm:px-5">
          <RunList
            runs={[...runs].sort(
              (left, right) =>
                new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
            )}
            now={now}
            hideScenario
          />
        </CardContent>
      </Card>
    </li>
  )
}

/**
 * One suite as an entity: current health first, then the evidence that needs
 * health first, then one scenario-first evidence narrative. Loaded runs live
 * beneath their scenario rather than being repeated as competing sections.
 * Suite-run context is presented as observed execution, never as authored
 * suite membership.
 */
export function SuiteDetailPage({
  runs,
  aggregates,
  groupKey,
  now
}: {
  runs: WebTestRun[]
  aggregates?: RunAggregateProjectionV2[]
  groupKey: string
  now: number
}) {
  const groups = useMemo(() => suiteSummaries(runs, aggregates), [aggregates, runs])
  const suite = groups.find((candidate) => candidate.key === groupKey)

  if (!suite) {
    return (
      <>
        <BackLink href="#/suites" label="Suites" />
        <header className="max-w-3xl">
          <h1 className="font-mono text-[28px] font-semibold tracking-tight break-words">
            {groupKey}
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">
            No runs are recorded for this suite. Its runs may have been retained out, or the link is
            stale.
          </p>
        </header>
      </>
    )
  }

  const progress = suite.progress
  const loaded = [...suite.runs].sort(
    (left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
  )
  const unloaded = Math.max(0, progress.total - loaded.length)
  const terminal = progress.total - progress.counts.active
  const passRate =
    terminal === 0 ? undefined : Math.round((progress.counts.passed / terminal) * 100)
  const isIndependentGroup = suite.key === independentSuiteKey
  const byScenario = new Map<string, WebTestRun[]>()
  for (const run of loaded) {
    byScenario.set(run.scenarioId, [...(byScenario.get(run.scenarioId) ?? []), run])
  }
  const observedScenarios = [...byScenario.entries()].sort(
    ([leftId, leftRuns], [rightId, rightRuns]) => {
      const rank = (runs: WebTestRun[]): number => {
        const latest = [...runs].sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )[0]!
        if (latest.status === 'failed' || latest.status === 'timed-out') return 0
        if (latest.status === 'needs-review') return 1
        if (latest.state !== 'terminal') return 2
        if (latest.status === 'infrastructure-error') return 3
        return 4
      }
      return rank(leftRuns) - rank(rightRuns) || leftId.localeCompare(rightId)
    }
  )

  return (
    <>
      <BackLink href="#/suites" label="Suites" />

      <header className="mb-8 max-w-3xl">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <SuiteAvatarTile
            id={suite.key}
            emojis={loaded.map((run) => scenarioEmoji(run.scenarioId))}
            size="large"
          />
          <h1 className="font-mono text-[28px] font-semibold tracking-tight break-words">
            {suite.label}
          </h1>
          <MetaLine
            items={[
              `${progress.total} run${progress.total === 1 ? '' : 's'}`,
              passRate === undefined
                ? undefined
                : `${passRate}% passed — ${progress.counts.passed} of ${terminal} finished run${terminal === 1 ? '' : 's'}`,
              suite.updatedAt ? `latest ${relativeTime(suite.updatedAt, now)}` : undefined
            ]}
          />
        </div>
        <div className="mt-4">
          <StatusDistribution progress={progress} />
        </div>
        <p className="mt-4 max-w-prose text-[13px] text-muted-foreground">
          {isIndependentGroup
            ? 'These runs started ad-hoc, outside any suite — this page groups them as execution context, not as a suite.'
            : 'One observed execution of this suite. Scenario membership below is what the loaded runs show, not the authored suite definition.'}
          {unloaded > 0
            ? ` ${unloaded} of its runs ${unloaded === 1 ? 'is' : 'are'} not loaded on this page, so parts of the picture are missing.`
            : ''}
        </p>
      </header>

      <section aria-label="Scenario evidence" className="max-w-3xl">
        <SectionLabel>Scenario evidence ({observedScenarios.length} observed)</SectionLabel>
        <p className="mt-2 mb-4 text-[13px] text-muted-foreground">
          {observedScenarios.length > 0
            ? `Each card is one reusable scenario; its loaded runs are evidence from this suite execution.`
            : 'None of this suite’s runs are loaded on this page, so the contributing scenarios are unknown here.'}
        </p>
        {observedScenarios.length > 0 ? (
          <ul className="space-y-3">
            {observedScenarios.map(([scenarioId, scenarioRuns]) => (
              <ScenarioEvidenceGroup
                key={scenarioId}
                scenarioId={scenarioId}
                runs={scenarioRuns}
                now={now}
              />
            ))}
          </ul>
        ) : null}
        {unloaded > 0 && loaded.length > 0 ? (
          <p className="mt-2 max-w-3xl text-[13px] text-muted-foreground">
            +{unloaded} earlier run{unloaded === 1 ? '' : 's'} of this suite{' '}
            {unloaded === 1 ? 'is' : 'are'} not loaded on this page.
          </p>
        ) : null}
      </section>
    </>
  )
}
